/**
 * Canvas-based comparison image renderer.
 *
 * Renders before/after comparison images (side-by-side or overlay) to an
 * offscreen canvas, matching the CSS preview transforms exactly, and returns
 * the result as a JPEG Blob.
 */
import { ImageTransform } from "@/types/notes";

import { authService } from "@/services/auth-service";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ComparisonRenderParams {
  beforeImageUrl: string;
  afterImageUrl: string;
  beforeTransform: ImageTransform;
  afterTransform: ImageTransform;
  mode: "side-by-side" | "overlay";
  /** 0-100, used only for overlay mode */
  sliderPosition: number;
  beforeName?: string;
  afterName?: string;
}

// ---------------------------------------------------------------------------
// Image loading helpers
// ---------------------------------------------------------------------------

/** Fetch a (potentially cross-origin) image URL via our server-side proxy
 *  so the resulting canvas is not tainted by CORS restrictions. */
async function fetchImageAsBlob(url: string): Promise<HTMLImageElement> {
  const proxyUrl = `/api/proxy-image?url=${encodeURIComponent(url)}`;
  const token = await authService.getToken();
  const res = await fetch(proxyUrl, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`Failed to fetch image: ${res.status}`);
  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);

  return new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error("Failed to load image from blob"));
    };
    img.src = blobUrl;
  });
}

// ---------------------------------------------------------------------------
// Transform helpers
// ---------------------------------------------------------------------------

/** Check if a transform has a crop region. */
function hasCrop(t: ImageTransform): boolean {
  return (
    t.cropX != null &&
    t.cropY != null &&
    t.cropWidth != null &&
    t.cropHeight != null &&
    (t.cropWidth ?? 0) > 0.01 &&
    (t.cropHeight ?? 0) > 0.01
  );
}

/**
 * Compute the "object-contain" rect for an image inside a container.
 * Returns the x, y, width, height at which the image should be drawn
 * so that it is fully visible, centred, with its aspect ratio preserved.
 */
function objectContainRect(
  imgW: number,
  imgH: number,
  containerW: number,
  containerH: number,
): { x: number; y: number; w: number; h: number } {
  const ratio = Math.min(containerW / imgW, containerH / imgH);
  const w = imgW * ratio;
  const h = imgH * ratio;
  return { x: (containerW - w) / 2, y: (containerH - h) / 2, w, h };
}

/**
 * Draw a single image with its transform into a rectangular region of the
 * canvas context.  The transform logic mirrors `getTransformStyle()` in
 * `image-compare-modal.tsx` so that the saved output matches the preview.
 *
 * CSS transform string is:
 *   rotate(deg) scaleX(mirror) scale(zoom) translate(panX%, panY%)
 * with transformOrigin = center center.
 *
 * When crop is active the CSS additionally folds in a baseScale and offset.
 */
function drawTransformedImage(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  transform: ImageTransform,
  regionX: number,
  regionY: number,
  regionW: number,
  regionH: number,
): void {
  ctx.save();

  // Clip to the target region so nothing bleeds outside
  ctx.beginPath();
  ctx.rect(regionX, regionY, regionW, regionH);
  ctx.clip();

  const centerX = regionX + regionW / 2;
  const centerY = regionY + regionH / 2;

  const mirrorScale = transform.mirror ? -1 : 1;
  const rotation = ((transform.rotation ?? 0) * Math.PI) / 180;

  // CSS transform: rotate(R) scaleX(M) scale(Z) translate(P)
  // Canvas CTM pre-multiplies each operation onto the current matrix, so
  // writing canvas operations in the same left-to-right order as the CSS
  // string produces an identical transformation matrix.

  if (hasCrop(transform)) {
    const cropX = transform.cropX ?? 0;
    const cropY = transform.cropY ?? 0;
    const cropW = transform.cropWidth ?? 1;
    const cropH = transform.cropHeight ?? 1;

    const baseScale = 1 / Math.min(cropW, cropH);
    const totalZoom = baseScale * transform.zoom;

    const cropCenterX = cropX + cropW / 2;
    const cropCenterY = cropY + cropH / 2;
    const offsetX = (0.5 - cropCenterX) * 100;
    const offsetY = (0.5 - cropCenterY) * 100;

    const panOffsetX = transform.panX * 100;
    const panOffsetY = transform.panY * 100;

    // Match CSS: rotate(R) scaleX(M) scale(Z) translate(offset+pan%)
    // Canvas operations written in the same left-to-right order produce
    // the same transformation matrix.
    ctx.translate(centerX, centerY);
    ctx.rotate(rotation);
    ctx.scale(mirrorScale, 1);
    ctx.scale(totalZoom, totalZoom);
    ctx.translate(
      ((offsetX + panOffsetX) / 100) * regionW,
      ((offsetY + panOffsetY) / 100) * regionH,
    );
    ctx.translate(-centerX, -centerY);
  } else {
    // Match CSS: rotate(R) scaleX(M) scale(Z) translate(pan%)
    ctx.translate(centerX, centerY);
    ctx.rotate(rotation);
    ctx.scale(mirrorScale, 1);
    ctx.scale(transform.zoom, transform.zoom);
    ctx.translate(transform.panX * regionW, transform.panY * regionH);
    ctx.translate(-centerX, -centerY);
  }

  // Draw the image using object-contain fitting within the region
  const { x, y, w, h } = objectContainRect(
    img.naturalWidth,
    img.naturalHeight,
    regionW,
    regionH,
  );
  ctx.drawImage(img, regionX + x, regionY + y, w, h);

  ctx.restore();
}

// ---------------------------------------------------------------------------
// Corner labels
// ---------------------------------------------------------------------------

function drawCornerLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  alignRight: boolean = false,
): void {
  ctx.save();
  const fontSize = 10;
  ctx.font = `${fontSize}px sans-serif`;
  const metrics = ctx.measureText(text);
  const textW = metrics.width;
  const textH = fontSize;
  const px = 2; // minimal padding

  let left: number;
  if (alignRight) {
    left = x - textW - px * 2;
  } else {
    left = x;
  }

  ctx.fillStyle = "rgba(0,0,0,0.7)";
  ctx.fillRect(left, y, textW + px * 2, textH + px * 2);

  ctx.fillStyle = "white";
  ctx.textBaseline = "top";
  ctx.fillText(text, left + px, y + px);
  ctx.restore();
}

// ---------------------------------------------------------------------------
// Comparison renderers
// ---------------------------------------------------------------------------

function renderSideBySide(
  canvas: HTMLCanvasElement,
  beforeImg: HTMLImageElement,
  afterImg: HTMLImageElement,
  beforeTransform: ImageTransform,
  afterTransform: ImageTransform,
): void {
  const ctx = canvas.getContext("2d")!;
  const halfW = canvas.width / 2;
  const h = canvas.height;

  // Black background
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, canvas.width, h);

  // Draw before on left half
  drawTransformedImage(ctx, beforeImg, beforeTransform, 0, 0, halfW, h);

  // Draw after on right half
  drawTransformedImage(ctx, afterImg, afterTransform, halfW, 0, halfW, h);

  // Separator line
  ctx.strokeStyle = "black";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(halfW, 0);
  ctx.lineTo(halfW, h);
  ctx.stroke();

  // Labels
  drawCornerLabel(ctx, "Before", 0, 0);
  drawCornerLabel(ctx, "After", canvas.width, 0, true);
}

function renderOverlay(
  canvas: HTMLCanvasElement,
  beforeImg: HTMLImageElement,
  afterImg: HTMLImageElement,
  beforeTransform: ImageTransform,
  afterTransform: ImageTransform,
  sliderPosition: number,
): void {
  const ctx = canvas.getContext("2d")!;
  const w = canvas.width;
  const h = canvas.height;
  const clipX = (w * sliderPosition) / 100;

  // Black background
  ctx.fillStyle = "#000";
  ctx.fillRect(0, 0, w, h);

  // Draw after image (full canvas)
  drawTransformedImage(ctx, afterImg, afterTransform, 0, 0, w, h);

  // Draw before image clipped to left of slider
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, clipX, h);
  ctx.clip();
  drawTransformedImage(ctx, beforeImg, beforeTransform, 0, 0, w, h);
  ctx.restore();

  // Slider line
  ctx.strokeStyle = "white";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(clipX, 0);
  ctx.lineTo(clipX, h);
  ctx.stroke();

  // Labels
  drawCornerLabel(ctx, "Before", 0, 0);
  drawCornerLabel(ctx, "After", w, 0, true);
}

// ---------------------------------------------------------------------------
// Main entry point
// ---------------------------------------------------------------------------

const MAX_CANVAS_DIMENSION = 4096;
const MAX_CANVAS_PIXELS = 12_000_000;

function computeCanvasBaseSize(
  imageWidth: number,
  imageHeight: number,
  widthMultiplier: number,
): { width: number; height: number } {
  const outputWidth = imageWidth * widthMultiplier;
  const outputPixels = outputWidth * imageHeight;
  const scale = Math.min(
    1,
    MAX_CANVAS_DIMENSION / outputWidth,
    MAX_CANVAS_DIMENSION / imageHeight,
    Math.sqrt(MAX_CANVAS_PIXELS / outputPixels),
  );

  return {
    width: Math.max(1, Math.floor(imageWidth * scale)),
    height: Math.max(1, Math.floor(imageHeight * scale)),
  };
}

// Photographic before/after content on a solid black background — JPEG keeps
// the encode buffer small (Safari rejects large PNG encodes far sooner) and
// produces a smaller upload with no meaningful visual loss.
const EXPORT_MIME_TYPE = "image/jpeg";
const EXPORT_QUALITY = 0.92;

export async function renderComparisonToCanvas(
  params: ComparisonRenderParams,
): Promise<Blob> {
  const [beforeImg, afterImg] = await Promise.all([
    fetchImageAsBlob(params.beforeImageUrl),
    fetchImageAsBlob(params.afterImageUrl),
  ]);

  // Determine canvas size from natural image dimensions
  const maxNatW = Math.max(beforeImg.naturalWidth, afterImg.naturalWidth);
  const maxNatH = Math.max(beforeImg.naturalHeight, afterImg.naturalHeight);

  const canvas = document.createElement("canvas");

  if (params.mode === "side-by-side") {
    const { width: baseW, height: baseH } = computeCanvasBaseSize(
      maxNatW,
      maxNatH,
      2,
    );
    canvas.width = baseW * 2;
    canvas.height = baseH;
    renderSideBySide(
      canvas,
      beforeImg,
      afterImg,
      params.beforeTransform,
      params.afterTransform,
    );
  } else {
    const { width: baseW, height: baseH } = computeCanvasBaseSize(
      maxNatW,
      maxNatH,
      1,
    );
    canvas.width = baseW;
    canvas.height = baseH;
    renderOverlay(
      canvas,
      beforeImg,
      afterImg,
      params.beforeTransform,
      params.afterTransform,
      params.sliderPosition,
    );
  }

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob returned null"));
      },
      EXPORT_MIME_TYPE,
      EXPORT_QUALITY,
    );
  });
}
